import { useState } from 'react'
import {
  Wallet,
  Edit,
  Pencil,
  Trash2,
  Send,
  CheckCircle2,
  Clock4,
  AlertCircle,
  Loader2,
  RefreshCw,
  Plus,
  CalendarIcon,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import {
  useGetVendorPaymentsQuery,
  useLazyGetVendorPaymentsQuery,
  useUpdateVendorPaymentAmountMutation,
  useUpdateVendorPaymentNameMutation,
  useDeleteVendorPaymentScheduleMutation,
  useSyncVendorPaymentMilestonesMutation,
  type VendorPayment,
  type VendorPaymentStatus,
} from '@/redux/service/vendor'
import {
  useUpdateProjectScopeMutation,
  useUpdateProjectMetaMutation,
} from '@/redux/service/project'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { Skeleton } from './ui/skeleton'
import { useCheckPermission } from '@/hooks/useCheckPermission'
import ReleasePaymentDialog from './ReleasePaymentDialog'
import { findFullPaymentRecord } from './vendorPaymentShared'

interface VendorPaymentScheduleProps {
  projectId: number
  vendorId: number
  vendorName: string
  paymentType: 'full' | 'milestone'
}

const STATUS_STYLES: Record<
  VendorPaymentStatus,
  { label: string; className: string; icon: typeof Clock4 }
> = {
  paid: {
    label: 'Paid',
    className:
      'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
    icon: CheckCircle2,
  },
  partial: {
    label: 'Partial',
    className:
      'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
    icon: Clock4,
  },
  pending: {
    label: 'Pending',
    className:
      'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
    icon: Clock4,
  },
  unset: {
    label: 'Amount not set',
    className: 'bg-muted text-muted-foreground border-border',
    icon: AlertCircle,
  },
}

const fmtDate = (dateString: string | null | undefined) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const StatusBadge = ({ status }: { status: VendorPaymentStatus }) => {
  const style = STATUS_STYLES[status]
  const StatusIcon = style.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border',
        style.className
      )}
    >
      <StatusIcon className='w-2.5 h-2.5' />
      {style.label}
    </span>
  )
}

const VendorPaymentSchedule = ({
  projectId,
  vendorId,
  vendorName,
  paymentType,
}: VendorPaymentScheduleProps) => {
  const [amountDialogOpen, setAmountDialogOpen] = useState(false)
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false)
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false)
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [releaseMode, setReleaseMode] = useState<'release' | 'markPaid'>(
    'release'
  )
  const [activePayment, setActivePayment] = useState<VendorPayment | null>(null)
  const { hasPageAccess } = useCheckPermission()
  /* Setting amounts and releasing payments is finance/admin-only - not
     mini_finance - because amounts are also masked from mini_finance in the
     response, so it shouldn't be able to write what it can't read back. */
  const canMutate = hasPageAccess('finance')
  const canViewCost = hasPageAccess('finance')

  const [updateAmount, { isLoading: isSavingAmount }] =
    useUpdateVendorPaymentAmountMutation()
  const [updateName, { isLoading: isSavingName }] =
    useUpdateVendorPaymentNameMutation()
  const [deleteSchedule, { isLoading: isDeleting }] =
    useDeleteVendorPaymentScheduleMutation()
  const [syncMilestones, { isLoading: isSyncing }] =
    useSyncVendorPaymentMilestonesMutation()
  /* Adding a milestone here goes through the same scope endpoint the
     Project Scope form uses (upsertProjectScope auto-pairs a schedule row
     when the vendor is on the milestone plan), so this list stays the
     source of truth instead of a separate milestone-creation path. */
  const [addMilestone, { isLoading: isAddingMilestone }] =
    useUpdateProjectScopeMutation()
  /* 'Full' vendors have no schedule row until one exists - updateProjectMeta
     auto-creates the single 'Full Project Payment' row server-side whenever
     it's sent a vendor_id + vendor_payment_type='full' (same auto-create
     path the vendor-linking flow already uses), so re-sending the project's
     current link here is enough to create it retroactively for vendors
     linked before that row existed. */
  const [ensureFullPaymentRecord, { isLoading: isCreatingRecord }] =
    useUpdateProjectMetaMutation()
  const [fetchVendorPayments, { isFetching: isFetchingNewRecord }] =
    useLazyGetVendorPaymentsQuery()

  // Refetch on every mount, not just when `projectId` changes. This
  // component mounts/remounts right after updateProjectMeta auto-creates
  // the 'full' schedule row server-side (linking a vendor, or switching
  // payment type) - the query key here doesn't include paymentType, so
  // without this a cached result from before that row existed can win the
  // race against the mutation's tag invalidation.
  const { data, isLoading, isError } = useGetVendorPaymentsQuery(
    { projectId },
    { skip: !projectId, refetchOnMountOrArgChange: true }
  )

  const isMilestoneMode = paymentType === 'milestone'
  const isPreparingNewRecord = isCreatingRecord || isFetchingNewRecord

  /* Single click for a brand-new 'full' vendor: create the schedule row,
     re-fetch to get its real id, then go straight into the same amount
     dialog an existing record uses - no separate "create" step to click
     through first. */
  const handleSetAmountForNewRecord = async () => {
    try {
      await ensureFullPaymentRecord({
        id: projectId,
        data: { vendor_id: vendorId, vendor_payment_type: 'full' },
      }).unwrap()
      const refreshed = await fetchVendorPayments({ projectId }).unwrap()
      const created = findFullPaymentRecord(refreshed.data)
      if (!created) {
        toast.error('Could not find the created payment record')
        return
      }
      openAmountDialog(created)
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to create payment record')
    }
  }

  const handleSyncMilestones = async () => {
    try {
      await syncMilestones({ projectId }).unwrap()
      toast.success('Milestones synced from ClickUp')
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to sync milestones')
    }
  }

  const rows = data?.data ?? []
  const isFullPayment = paymentType === 'full'
  const fullPayment = isFullPayment ? findFullPaymentRecord(rows) : rows[0]
  const colCount = canViewCost ? (canMutate ? 6 : 5) : 3

  const openAmountDialog = (payment: VendorPayment) => {
    setActivePayment(payment)
    setAmountDialogOpen(true)
  }

  const openReleaseDialog = (
    payment: VendorPayment,
    mode: 'release' | 'markPaid'
  ) => {
    setActivePayment(payment)
    setReleaseMode(mode)
    setReleaseDialogOpen(true)
  }

  const canReleaseFor = (payment: VendorPayment) =>
    canMutate && payment.amount != null && (payment.balance ?? 0) > 0

  const openRenameDialog = (payment: VendorPayment) => {
    setActivePayment(payment)
    setRenameDialogOpen(true)
  }

  const openDeleteConfirm = (payment: VendorPayment) => {
    setActivePayment(payment)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteMilestone = async () => {
    if (!activePayment) return
    try {
      await deleteSchedule({
        projectId,
        id: activePayment.id,
      }).unwrap()
      toast.success('Milestone deleted')
      setDeleteConfirmOpen(false)
      setActivePayment(null)
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to delete milestone')
    }
  }

  return (
    <div className='space-y-4'>
      {/* ── Header ── */}
      <div className='flex items-center justify-between w-full flex-wrap gap-3'>
        {/* Left Side */}
        <div className='flex items-center gap-3 flex-wrap flex-1'>
          <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-500/10 border border-orange-500/20'>
            <Wallet className='w-4 h-4 text-orange-500' />
            <span className='text-sm font-semibold text-orange-500'>
              {isFullPayment
                ? 'VENDOR PAYMENT SUMMARY'
                : 'VENDOR PAYMENT SCHEDULE'}
            </span>
          </div>

          <span className='text-lg text-muted-foreground font-medium'>
            {vendorName}
          </span>
        </div>

        {/* Right Side */}
        {isMilestoneMode && canMutate && (
          <div className='flex items-center gap-2 ml-auto'>
            <Button
              size='sm'
              className='h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white'
              onClick={() => setAddMilestoneOpen(true)}
            >
              <Plus className='w-4 h-4 mr-1' />
              Add Milestone
            </Button>

            <Button
              size='sm'
              variant='outline'
              className='h-8 px-3'
              onClick={handleSyncMilestones}
              disabled={isSyncing}
            >
              {isSyncing ? (
                <Loader2 className='w-4 h-4 mr-1 animate-spin' />
              ) : (
                <RefreshCw className='w-4 h-4 mr-1' />
              )}
              Sync Milestones
            </Button>
          </div>
        )}
      </div>

      {isFullPayment ? (
        /* ── Full payment: single lump-sum summary, not a milestone table ── */
        <div className='border border-border rounded-lg bg-card shadow-sm p-5'>
          {isLoading ? (
            <Skeleton className='h-16 w-full rounded-md' />
          ) : isError ? (
            <div className='flex flex-col items-center justify-center gap-1.5 py-4'>
              <AlertCircle className='w-7 h-7 text-destructive/50' />
              <p className='text-sm font-medium text-muted-foreground'>
                Failed to load payment
              </p>
            </div>
          ) : fullPayment ? (
            <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
              {canViewCost ? (
                <div className='grid grid-cols-3 gap-6'>
                  <div>
                    <p className='text-[10px] uppercase tracking-wide text-muted-foreground font-semibold'>
                      Amount
                    </p>
                    <p className='text-lg font-bold text-foreground'>
                      {fullPayment.amount != null
                        ? `$${Number(fullPayment.amount).toFixed(2)}`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-[10px] uppercase tracking-wide text-muted-foreground font-semibold'>
                      Released
                    </p>
                    <p className='text-lg font-bold text-foreground'>
                      {fullPayment.amount != null
                        ? `$${Number(fullPayment.released ?? 0).toFixed(2)}`
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <p className='text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1'>
                      Status
                    </p>
                    <StatusBadge status={fullPayment.status ?? 'unset'} />
                  </div>
                </div>
              ) : (
                <p className='text-sm text-muted-foreground'>
                  Full project payment - {vendorName}
                </p>
              )}
              {canMutate && (
                <div className='flex items-center gap-2 shrink-0'>
                  <Button
                    size='sm'
                    variant='outline'
                    className='h-8 text-xs'
                    onClick={() => openAmountDialog(fullPayment)}
                  >
                    <Edit className='w-3.5 h-3.5 mr-1.5' />
                    {fullPayment.amount != null ? 'Edit Amount' : 'Set Amount'}
                  </Button>
                  {canReleaseFor(fullPayment) && (
                    <>
                      <Button
                        size='sm'
                        variant='outline'
                        className='h-8 text-xs'
                        onClick={() =>
                          openReleaseDialog(fullPayment, 'release')
                        }
                      >
                        <Send className='w-3.5 h-3.5 mr-1.5' />
                        Release Payment
                      </Button>
                      <Button
                        size='sm'
                        className='h-8 text-xs bg-green-600 hover:bg-green-700 text-white'
                        onClick={() =>
                          openReleaseDialog(fullPayment, 'markPaid')
                        }
                      >
                        <CheckCircle2 className='w-3.5 h-3.5 mr-1.5' />
                        Mark as Paid
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center gap-3 py-4'>
              <p className='text-sm text-muted-foreground'>
                No payment record yet for this vendor.
              </p>
              {canMutate && (
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleSetAmountForNewRecord}
                  disabled={isPreparingNewRecord}
                  className='h-8 text-xs'
                >
                  {isPreparingNewRecord ? (
                    <Loader2 className='w-3.5 h-3.5 mr-1.5 animate-spin' />
                  ) : (
                    <Edit className='w-3.5 h-3.5 mr-1.5' />
                  )}
                  Set Amount
                </Button>
              )}
            </div>
          )}
        </div>
      ) : (
        /* ── Milestone payment: one row per project milestone ── */
        <div className='border border-border rounded-sm bg-card shadow-sm overflow-hidden'>
          <div className='overflow-x-auto'>
            <Table className='border-none'>
              <TableHeader>
                <TableRow className='border-b border-border bg-muted/40 hover:bg-muted/40'>
                  <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                    Milestone
                  </TableHead>
                  <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                    Dates
                  </TableHead>
                  {canViewCost && (
                    <>
                      <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                        Amount
                      </TableHead>
                      <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                        Released
                      </TableHead>
                      <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap px-4'>
                        Status
                      </TableHead>
                    </>
                  )}
                  {canMutate && (
                    <TableHead className='text-xs font-semibold uppercase tracking-wide text-muted-foreground text-right whitespace-nowrap px-4'>
                      Actions
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i} className='border-b border-border/50'>
                      {[...Array(colCount)].map((_, j) => (
                        <TableCell key={j} className='px-4 py-3'>
                          <Skeleton className='h-4 w-full rounded-md' />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell colSpan={colCount} className='h-28 text-center'>
                      <div className='flex flex-col items-center justify-center gap-1.5'>
                        <AlertCircle className='w-7 h-7 text-destructive/50' />
                        <p className='text-sm font-medium text-muted-foreground'>
                          Failed to load payment schedule
                        </p>
                        <p className='text-xs text-muted-foreground/60'>
                          Something went wrong. Try refreshing.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : rows.length > 0 ? (
                  rows.map(payment => (
                    <TableRow
                      key={payment.id}
                      className='border-b border-border/50 hover:bg-accent/30 transition-colors duration-150 group'
                    >
                      <TableCell className='px-4 py-3 text-sm font-medium'>
                        {payment.milestone_name}
                      </TableCell>
                      <TableCell className='px-4 py-3 text-xs text-muted-foreground whitespace-nowrap'>
                        {payment.start_date || payment.end_date
                          ? `${fmtDate(payment.start_date)} – ${fmtDate(payment.end_date)}`
                          : '—'}
                      </TableCell>
                      {canViewCost && (
                        <>
                          <TableCell className='px-4 py-3 text-sm font-medium'>
                            {payment.amount != null
                              ? `$${Number(payment.amount).toFixed(2)}`
                              : '—'}
                          </TableCell>
                          <TableCell className='px-4 py-3 text-sm'>
                            {payment.amount != null
                              ? `$${Number(payment.released ?? 0).toFixed(2)}`
                              : '—'}
                          </TableCell>
                          <TableCell className='px-4 py-3'>
                            <StatusBadge status={payment.status ?? 'unset'} />
                          </TableCell>
                        </>
                      )}
                      {canMutate && (
                        <TableCell className='px-4 py-3'>
                          <div className='flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
                            <Button
                              size='icon'
                              variant='ghost'
                              className='h-7 w-7 hover:bg-accent'
                              onClick={() => openRenameDialog(payment)}
                              title='Rename milestone'
                            >
                              <Pencil className='w-3.5 h-3.5' />
                            </Button>
                            <Button
                              size='icon'
                              variant='ghost'
                              className='h-7 w-7 hover:bg-accent'
                              onClick={() => openAmountDialog(payment)}
                              title='Set / edit amount'
                            >
                              <Edit className='w-3.5 h-3.5' />
                            </Button>
                            {canReleaseFor(payment) && (
                              <>
                                <Button
                                  size='icon'
                                  variant='ghost'
                                  className='h-7 w-7 hover:bg-accent'
                                  onClick={() =>
                                    openReleaseDialog(payment, 'release')
                                  }
                                  title='Release payment'
                                >
                                  <Send className='w-3.5 h-3.5' />
                                </Button>
                                <Button
                                  size='icon'
                                  variant='ghost'
                                  className='h-7 w-7 text-green-600 hover:bg-green-500/10'
                                  onClick={() =>
                                    openReleaseDialog(payment, 'markPaid')
                                  }
                                  title='Mark as paid'
                                >
                                  <CheckCircle2 className='w-3.5 h-3.5' />
                                </Button>
                              </>
                            )}
                            {(payment.released ?? 0) <= 0 && (
                              <Button
                                size='icon'
                                variant='ghost'
                                className='h-7 w-7 text-destructive/70 hover:text-destructive hover:bg-destructive/10'
                                onClick={() => openDeleteConfirm(payment)}
                                title='Delete milestone'
                              >
                                <Trash2 className='w-3.5 h-3.5' />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={colCount} className='h-28 text-center'>
                      <div className='flex flex-col items-center justify-center gap-1.5'>
                        <Wallet className='w-7 h-7 text-muted-foreground/40' />
                        <p className='text-sm font-medium text-muted-foreground'>
                          No milestones yet
                        </p>
                        <p className='text-xs text-muted-foreground/60'>
                          Click &quot;Add Milestone&quot; above, or &quot;Sync
                          Milestones&quot; to pull them from ClickUp.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* ── Set / edit amount dialog ── */}
      <Dialog open={amountDialogOpen} onOpenChange={setAmountDialogOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
                <Edit className='w-4 h-4 text-orange-500' />
              </div>
              <div>
                <DialogTitle className='text-base font-semibold leading-tight'>
                  Set Payment Amount
                </DialogTitle>
                <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                  {activePayment?.milestone_name}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Formik
            key={activePayment?.id ?? 'none'}
            initialValues={{ amount: activePayment?.amount ?? '' }}
            enableReinitialize
            validationSchema={Yup.object({
              amount: Yup.number()
                .typeError('Must be a number')
                .min(
                  activePayment?.released ?? 0,
                  `Cannot be below the ${(activePayment?.released ?? 0).toFixed(2)} already released`
                )
                .required('Amount is required'),
            })}
            onSubmit={async (values, { resetForm }) => {
              if (!activePayment) return
              try {
                await updateAmount({
                  projectId,
                  id: activePayment.id,
                  amount: Number(values.amount),
                }).unwrap()
                toast.success('Payment amount updated')
                resetForm()
                setAmountDialogOpen(false)
              } catch (err: unknown) {
                const error = err as { data?: { message?: string } }
                toast.error(error?.data?.message ?? 'Failed to save')
              }
            }}
          >
            {({ handleSubmit, handleChange, values, errors, touched }) => (
              <div className='px-6 py-5 space-y-4'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='vp-set-amount'
                    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                  >
                    Amount ($) <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='vp-set-amount'
                    name='amount'
                    type='number'
                    min={0}
                    step='0.01'
                    placeholder='0.00'
                    value={values.amount}
                    onChange={handleChange}
                    className={cn(
                      'h-9 text-sm',
                      errors.amount && touched.amount
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : ''
                    )}
                  />
                  {errors.amount && touched.amount && (
                    <p className='text-xs text-destructive'>
                      {String(errors.amount)}
                    </p>
                  )}
                </div>

                <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setAmountDialogOpen(false)}
                    className='h-8 text-xs border-border hover:bg-accent/30'
                  >
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    onClick={() => handleSubmit()}
                    disabled={isSavingAmount}
                    className='h-8 text-xs min-w-[80px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                  >
                    {isSavingAmount ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* ── Release / mark-as-paid dialog ── */}
      <ReleasePaymentDialog
        open={releaseDialogOpen}
        onClose={() => setReleaseDialogOpen(false)}
        projectId={projectId}
        payment={activePayment}
        mode={releaseMode}
      />

      {/* ── Rename milestone dialog ── */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
                <Pencil className='w-4 h-4 text-orange-500' />
              </div>
              <DialogTitle className='text-base font-semibold leading-tight'>
                Rename Milestone
              </DialogTitle>
            </div>
          </DialogHeader>

          <Formik
            key={activePayment?.id ?? 'none'}
            initialValues={{ name: activePayment?.milestone_name ?? '' }}
            enableReinitialize
            validationSchema={Yup.object({
              name: Yup.string().trim().required('Milestone name is required'),
            })}
            onSubmit={async (values, { resetForm }) => {
              if (!activePayment) return
              try {
                await updateName({
                  projectId,
                  id: activePayment.id,
                  milestone_name: values.name,
                }).unwrap()
                toast.success('Milestone renamed')
                resetForm()
                setRenameDialogOpen(false)
              } catch (err: unknown) {
                const error = err as { data?: { message?: string } }
                toast.error(
                  error?.data?.message ?? 'Failed to rename milestone'
                )
              }
            }}
          >
            {({ handleSubmit, handleChange, values, errors, touched }) => (
              <div className='px-6 py-5 space-y-4'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='rn-name'
                    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                  >
                    Milestone Name <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='rn-name'
                    name='name'
                    value={values.name}
                    onChange={handleChange}
                    className={cn(
                      'h-9 text-sm',
                      errors.name && touched.name
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : ''
                    )}
                  />
                  {errors.name && touched.name && (
                    <p className='text-xs text-destructive'>{errors.name}</p>
                  )}
                </div>

                <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setRenameDialogOpen(false)}
                    className='h-8 text-xs border-border hover:bg-accent/30'
                  >
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    onClick={() => handleSubmit()}
                    disabled={isSavingName}
                    className='h-8 text-xs min-w-[80px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                  >
                    {isSavingName ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* ── Delete milestone confirmation ── */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-destructive/10 border border-destructive/20 shrink-0'>
                <Trash2 className='w-4 h-4 text-destructive' />
              </div>
              <DialogTitle className='text-base font-semibold'>
                Delete Milestone
              </DialogTitle>
            </div>
          </DialogHeader>
          <div className='px-6 py-5'>
            <p className='text-sm text-muted-foreground'>
              Delete &quot;{activePayment?.milestone_name}&quot; from the
              payment schedule? This does not delete the ClickUp task or Project
              Scope milestone it's paired with, only the payment tracking for
              it. This action cannot be undone.
            </p>
          </div>
          <div className='flex justify-end gap-2 px-6 pb-5'>
            <Button
              variant='outline'
              size='sm'
              className='h-8 text-xs border-border hover:bg-accent/30'
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              size='sm'
              className='h-8 text-xs'
              disabled={isDeleting}
              onClick={handleDeleteMilestone}
            >
              {isDeleting ? (
                <>
                  <Loader2 className='w-3 h-3 mr-1.5 animate-spin' />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Add milestone dialog ── */}
      <Dialog open={addMilestoneOpen} onOpenChange={setAddMilestoneOpen}>
        <DialogContent className='max-w-sm p-0 gap-0'>
          <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
            <div className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
                <Plus className='w-4 h-4 text-orange-500' />
              </div>
              <div>
                <DialogTitle className='text-base font-semibold leading-tight'>
                  Add Milestone
                </DialogTitle>
                <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                  {vendorName}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <Formik
            initialValues={{ name: '', start_date: '', end_date: '' }}
            validationSchema={Yup.object({
              name: Yup.string().trim().required('Milestone name is required'),
              start_date: Yup.string().required('Start date is required'),
              end_date: Yup.string()
                .required('End date is required')
                .test(
                  'after-start',
                  'End date cannot be before start date',
                  function (end) {
                    const { start_date } = this.parent
                    if (!start_date || !end) return true
                    return new Date(end) >= new Date(start_date)
                  }
                ),
            })}
            onSubmit={async (values, { resetForm }) => {
              try {
                await addMilestone({
                  project_id: projectId,
                  data: {
                    teams: [],
                    milestones: [
                      {
                        name: values.name,
                        is_completed: false,
                        start_date: values.start_date,
                        end_date: values.end_date,
                      },
                    ],
                  },
                }).unwrap()
                toast.success('Milestone added')
                resetForm()
                setAddMilestoneOpen(false)
              } catch (err: unknown) {
                const error = err as { data?: { message?: string } }
                toast.error(error?.data?.message ?? 'Failed to add milestone')
              }
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              setFieldValue,
            }) => (
              <div className='px-6 py-5 space-y-4'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='am-name'
                    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                  >
                    Milestone Name <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='am-name'
                    name='name'
                    placeholder='e.g. Phase 1 delivery'
                    value={values.name}
                    onChange={handleChange}
                    className={cn(
                      'h-9 text-sm',
                      errors.name && touched.name
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : ''
                    )}
                  />
                  {errors.name && touched.name && (
                    <p className='text-xs text-destructive'>{errors.name}</p>
                  )}
                </div>

                <div className='grid grid-cols-2 gap-3'>
                  <div className='space-y-1.5'>
                    <Label className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      Start Date <span className='text-destructive'>*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          className={cn(
                            'h-9 w-full justify-start text-left font-normal text-sm',
                            !values.start_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className='mr-1.5 h-3.5 w-3.5 opacity-50 shrink-0' />
                          {values.start_date
                            ? format(new Date(values.start_date), 'PP')
                            : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={
                            values.start_date
                              ? new Date(values.start_date)
                              : undefined
                          }
                          onSelect={date =>
                            setFieldValue(
                              'start_date',
                              date ? format(date, 'yyyy-MM-dd') : ''
                            )
                          }
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.start_date && touched.start_date && (
                      <p className='text-xs text-destructive'>
                        {errors.start_date}
                      </p>
                    )}
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      End Date <span className='text-destructive'>*</span>
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          className={cn(
                            'h-9 w-full justify-start text-left font-normal text-sm',
                            !values.end_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className='mr-1.5 h-3.5 w-3.5 opacity-50 shrink-0' />
                          {values.end_date
                            ? format(new Date(values.end_date), 'PP')
                            : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={
                            values.end_date
                              ? new Date(values.end_date)
                              : undefined
                          }
                          onSelect={date =>
                            setFieldValue(
                              'end_date',
                              date ? format(date, 'yyyy-MM-dd') : ''
                            )
                          }
                          disabled={date =>
                            values.start_date
                              ? date < new Date(values.start_date)
                              : false
                          }
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.end_date && touched.end_date && (
                      <p className='text-xs text-destructive'>
                        {errors.end_date}
                      </p>
                    )}
                  </div>
                </div>

                <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setAddMilestoneOpen(false)}
                    className='h-8 text-xs border-border hover:bg-accent/30'
                  >
                    Cancel
                  </Button>
                  <Button
                    size='sm'
                    onClick={() => handleSubmit()}
                    disabled={isAddingMilestone}
                    className='h-8 text-xs min-w-[80px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                  >
                    {isAddingMilestone ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      'Add'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default VendorPaymentSchedule
